import { parseJsonPayload } from './parseJsonPayload';

const DEFAULT_OPTIONS = {
  autoReconnect: true,
  reconnectBaseMs: 1000,
  reconnectMaxMs: 30000,
  heartbeatMs: 30000,
};

export function createReconnectingSocket(url, options = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let ws = null;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let reconnectAttempt = 0;
  let manualClose = false;
  let status = 'Disconnected';
  const outboundQueue = [];

  const listeners = {
    message: new Set(),
    status: new Set(),
    error: new Set(),
  };

  function setStatus(next) {
    status = next;
    listeners.status.forEach((fn) => fn(next));
  }

  function notifyMessage(payload) {
    listeners.message.forEach((fn) => fn(payload));
  }

  function flushQueue() {
    while (outboundQueue.length && ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(outboundQueue.shift()));
    }
  }

  function scheduleReconnect() {
    if (!config.autoReconnect || manualClose) return;
    const delay = Math.min(
      config.reconnectBaseMs * 2 ** reconnectAttempt,
      config.reconnectMaxMs
    );
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(connect, delay);
  }

  function startHeartbeat() {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'pong' }));
      }
    }, config.heartbeatMs);
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function connect() {
    if (!url) return;

    manualClose = false;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (ws) {
      ws.close(1000, 'Reconnecting');
      ws = null;
    }

    setStatus('Connecting');

    try {
      ws = new WebSocket(url);

      ws.onopen = () => {
        reconnectAttempt = 0;
        setStatus('Connected');
        flushQueue();
        startHeartbeat();
        if (typeof config.onOpen === 'function') {
          config.onOpen();
        }
      };

      ws.onmessage = (event) => {
        const payload = parseJsonPayload(event.data);
        if (!payload) return;

        if (payload.type === 'ping') {
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'pong' }));
          }
          return;
        }

        notifyMessage(payload);
      };

      ws.onclose = (event) => {
        stopHeartbeat();
        ws = null;
        setStatus('Disconnected');

        if (event.code === 4401) {
          window.location.href = '/r/session-expired';
          return;
        }

        if (!manualClose && event.code !== 1000) {
          scheduleReconnect();
        }
      };

      ws.onerror = (error) => {
        listeners.error.forEach((fn) => fn(error));
        setStatus('Error');
      };
    } catch (error) {
      listeners.error.forEach((fn) => fn(error));
      setStatus('Error');
      scheduleReconnect();
    }
  }

  function send(data) {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
      return true;
    }
    if (status === 'Connecting') {
      outboundQueue.push(data);
      return true;
    }
    return false;
  }

  function disconnect() {
    manualClose = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    stopHeartbeat();
    outboundQueue.length = 0;
    if (ws) {
      ws.close(1000, 'Manual disconnect');
      ws = null;
    }
    setStatus('Disconnected');
  }

  function on(event, handler) {
    if (listeners[event]) listeners[event].add(handler);
    return () => listeners[event]?.delete(handler);
  }

  connect();

  return {
    connect,
    disconnect,
    send,
    on,
    getStatus: () => status,
  };
}
